/* eslint react/prop-types: 0 */
import React, { useState, useEffect, useCallback } from 'react';
import { SEOHead } from '@/components/seo';
import { Header, Footer, useGuideRequest } from '@/components/public';
import { AiEnquiryModal } from '@/components/public/AiEnquiryModal';
import {
  ArrowRight, Mic, Video, Image as ImageIcon, Search, Code2, SlidersHorizontal,
  Repeat, Database, Cpu, Bot, ShieldAlert, Scale, FileText, Layers, Lock, RefreshCcw,
  Building2, Mail, CheckCircle2, Clock, PenLine, ShieldCheck, Stamp, Download, ChevronRight,
} from 'lucide-react';

// ── Palette (identical to /investment-drafting) ───────────────────────────────
const BG = 'bg-[#0b1310]';
const BG_PANEL = 'bg-[#111c17]';
const BORDER = 'border-[#1e2a24]';

// ── Reusable subcomponents (inline duplication per locked decision A) ────────
const SectionLabel = ({ children }) => (
  <span className="text-[#059669] font-semibold text-xs uppercase tracking-[0.18em]">
    {children}
  </span>
);

const DocBullet = ({ children }) => (
  <li className="flex items-start gap-3 py-1.5">
    <span
      className="w-1.5 h-1.5 rounded-full bg-[#059669] shrink-0 mt-2"
      aria-hidden="true"
    />
    <span className="text-sm text-slate-300 leading-relaxed">{children}</span>
  </li>
);

const Step = ({ n, title, body, testId }) => (
  <div className="flex gap-5" data-testid={testId}>
    <div
      className="w-10 h-10 rounded-full bg-[#059669] text-white flex items-center justify-center text-base font-bold shrink-0"
      aria-hidden="true"
    >
      {n}
    </div>
    <div>
      <h4 className="text-lg font-semibold text-white mb-1.5">{title}</h4>
      <p className="text-sm text-slate-400 leading-relaxed">{body}</p>
    </div>
  </div>
);

const PrimaryCTA = ({ testId, children, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="inline-flex items-center gap-2 bg-[#059669] text-white font-semibold text-sm md:text-base px-6 py-3.5 rounded-lg hover:bg-[#047857] transition-colors"
    data-testid={testId}
  >
    {children} <ArrowRight className="w-4 h-4" />
  </button>
);

// ── Accordion — used by "Find Your AI" (12 categories) and "Every AI Company" (3 layers)
// Supports deep-linking via URL hash: /ai-document-services#voice opens & scrolls the item.
// - First item open by default on desktop (>=768px); all collapsed on mobile.
// - Hash change (in-page nav + external link) → opens matching item + scrolls into view.
// - Keyboard accessible (native <button> handles Enter/Space).
const AiAccordion = ({ items, testIdPrefix }) => {
  const [openIds, setOpenIds] = useState(() => {
    // SSR-safe initial state
    if (typeof window === 'undefined') return new Set();
    const hash = (window.location.hash || '').replace('#', '');
    if (hash && items.some((it) => it.id === hash)) return new Set([hash]);
    // Desktop default: first item open. Mobile default: all closed.
    if (window.matchMedia('(min-width: 768px)').matches && items.length > 0) {
      return new Set([items[0].id]);
    }
    return new Set();
  });

  const openAndScroll = useCallback((id) => {
    if (!items.some((it) => it.id === id)) return;
    setOpenIds((cur) => {
      const next = new Set(cur);
      next.add(id);
      return next;
    });
    // Wait for the accordion to expand before scrolling.
    setTimeout(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 60);
  }, [items]);

  useEffect(() => {
    // Handle hash present on mount (SPA route change that already set hash)
    const initialHash = (window.location.hash || '').replace('#', '');
    if (initialHash && items.some((it) => it.id === initialHash)) {
      openAndScroll(initialHash);
    }
    const onHashChange = () => {
      const h = (window.location.hash || '').replace('#', '');
      if (h) openAndScroll(h);
    };
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, [items, openAndScroll]);

  const toggle = (id) => {
    setOpenIds((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className={`${BG_PANEL} border ${BORDER} rounded-xl overflow-hidden`} data-testid={`${testIdPrefix}-accordion`}>
      {items.map((it, i) => {
        const isOpen = openIds.has(it.id);
        return (
          <div
            key={it.id}
            id={it.id}
            className={`${i < items.length - 1 ? `border-b ${BORDER}` : ''} scroll-mt-24`}
            data-testid={`${testIdPrefix}-item-${it.id}`}
          >
            <button
              type="button"
              onClick={() => toggle(it.id)}
              aria-expanded={isOpen}
              aria-controls={`${it.id}-panel`}
              className="w-full flex items-start gap-4 px-6 py-5 text-left hover:bg-white/[0.02] transition-colors"
              data-testid={`${testIdPrefix}-head-${it.id}`}
            >
              <div className="flex-1 min-w-0">
                <span className="block text-[15.5px] font-semibold text-white">{it.title}</span>
                {it.trigger && (
                  <span className="block text-[12.5px] mt-1" style={{ color: '#34d399' }}>
                    {it.trigger}
                  </span>
                )}
              </div>
              <ChevronRight
                className={`w-4 h-4 shrink-0 mt-1 transition-transform ${isOpen ? 'rotate-90 text-[#059669]' : 'text-slate-400'}`}
                aria-hidden="true"
              />
            </button>
            {isOpen && (
              <ul
                id={`${it.id}-panel`}
                className="px-6 pb-5 pt-0.5 space-y-0"
                data-testid={`${testIdPrefix}-body-${it.id}`}
              >
                {it.documents.map((d) => (
                  <li
                    key={d}
                    className={`text-sm text-slate-400 py-2 border-b ${BORDER} last:border-b-0 flex items-start gap-2.5`}
                  >
                    <span className="font-mono text-[#059669] shrink-0">—</span>
                    <span>{d}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        );
      })}
    </div>
  );
};

// ── Data Room feature card (inline duplicate of ReasonCard) ──────────────────
const ReasonCard = ({ title, body, testId }) => (
  <div
    className={`${BG_PANEL} ${BORDER} border rounded-xl p-7 hover:border-[#059669]/40 transition-colors`}
    data-testid={testId}
  >
    <h3 className="text-lg font-semibold text-white mb-3">{title}</h3>
    <p className="text-sm text-slate-400 leading-relaxed">{body}</p>
  </div>
);

// ── StepBlock (text + mockup, alternating) — inline duplicate ────────────────
const StepBlock = ({ number, title, body, mockup, mockupOnRightDesktop, testId }) => {
  const textOrder = mockupOnRightDesktop ? 'md:order-1' : 'md:order-2';
  const mockupOrder = mockupOnRightDesktop ? 'md:order-2' : 'md:order-1';
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-14 items-center" data-testid={testId}>
      <div className={`${textOrder}`}>
        <p className="text-6xl md:text-7xl font-bold text-[#059669]/20 tracking-tighter leading-none mb-4">
          {number}
        </p>
        <h3 className="font-serif text-2xl md:text-3xl font-semibold text-white tracking-tight leading-tight mb-4">
          {title}
        </h3>
        <p className="text-base md:text-lg text-slate-400 leading-relaxed">{body}</p>
      </div>
      <div className={`${mockupOrder}`}>{mockup}</div>
    </div>
  );
};

const StatusBadge = ({ tone, children }) => {
  const map = {
    draft:    'bg-amber-500/15 text-amber-300 border-amber-500/30',
    pending:  'bg-slate-500/15 text-slate-300 border-slate-500/30',
    approved: 'bg-[#059669]/15 text-[#059669] border-[#059669]/30',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide border ${map[tone]}`}>
      {children}
    </span>
  );
};

// ─── Mockup 1: Document list (Step 01) ──────────────────────────────────────
const DocumentListMockup = () => {
  const docs = [
    { title: 'Voice Cloning Consent — Production', sub: 'Voice-actor consent + likeness license for v3 model', v: 'v2' },
    { title: 'Acceptable Use Policy — Public API', sub: 'AUP for the developer API endpoints', v: 'v1' },
    { title: 'DPA — Enterprise Customer (Acme)', sub: 'Data Processing Agreement with no-training warranty', v: 'v1' },
  ];
  return (
    <div className="bg-[#111c17] border border-[#1e2a24] rounded-xl shadow-2xl overflow-hidden" data-testid="ai-mockup-document-list">
      <div className="px-5 py-4 border-b border-[#1e2a24] flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-md bg-[#059669]/15 flex items-center justify-center">
            <Building2 className="w-4 h-4 text-[#059669]" />
          </div>
          <div>
            <p className="text-white font-semibold text-sm leading-none">Acme AI Inc.</p>
            <span className="text-[10px] text-slate-500">C-Corporation · Delaware</span>
          </div>
        </div>
        <span className="text-[10px] font-semibold uppercase tracking-wide text-[#059669] bg-[#059669]/10 border border-[#059669]/30 px-2 py-1 rounded">C-Corp</span>
      </div>
      <div className="px-5 pt-3 flex items-center gap-5 border-b border-[#1e2a24]">
        <span className="text-sm font-semibold text-white pb-2 border-b-2 border-[#059669]">Documents</span>
        <span className="text-sm text-slate-500 pb-2">Activity Log</span>
      </div>
      <ul className="divide-y divide-[#1e2a24]">
        {docs.map((d, i) => (
          <li key={i} className="px-5 py-3.5 hover:bg-white/[0.02]">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-white truncate">{d.title}</p>
                <p className="text-xs text-slate-500 mt-0.5 truncate">{d.sub}</p>
              </div>
              <span className="text-[10px] font-mono text-slate-400 shrink-0">{d.v}</span>
            </div>
            <div className="mt-2 flex items-center justify-between">
              <StatusBadge tone="draft">draft</StatusBadge>
              <span className="text-[10px] text-slate-500">Jun 20, 2026</span>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};

// ─── Mockup 2: Email invite + OTP (Step 02) ─────────────────────────────────
const EmailInviteAndOtpMockup = () => (
  <div className="space-y-5">
    <div className="bg-[#111c17] border border-[#1e2a24] rounded-xl shadow-2xl overflow-hidden" data-testid="ai-mockup-email-invite">
      <div className="px-5 py-3 border-b border-[#1e2a24] flex items-center gap-2 text-slate-500 text-xs">
        <Mail className="w-3.5 h-3.5" />
        <span>From: notifications@starteaseai.com</span>
      </div>
      <div className="px-5 py-5">
        <p className="text-base font-semibold text-white mb-3">You&apos;ve been invited to review a document</p>
        <p className="text-xs text-slate-400 mb-1"><span className="text-slate-500">Company:</span> Acme AI Inc.</p>
        <p className="text-xs text-slate-400 mb-4"><span className="text-slate-500">Document:</span> Voice Cloning Consent — Production</p>
        <p className="text-sm text-slate-300 leading-relaxed mb-4">
          Click below to access the document. You&apos;ll receive a 6-digit OTP to log in — no account or password needed.
        </p>
        <span className="inline-flex items-center gap-2 bg-[#059669] text-white font-semibold text-sm px-4 py-2.5 rounded-lg">
          Review Document <ArrowRight className="w-3.5 h-3.5" />
        </span>
        <p className="text-[10px] text-slate-600 mt-5">Powered by StartEase Agent</p>
      </div>
    </div>
    <div className="bg-[#111c17] border border-[#1e2a24] rounded-xl shadow-2xl px-5 py-6" data-testid="ai-mockup-otp-login">
      <h4 className="text-base font-semibold text-white mb-1">Enter your access code</h4>
      <p className="text-xs text-slate-500 mb-4">We sent a 6-digit code to talent@acmeai.com</p>
      <div className="flex items-center gap-2 mb-4">
        {['4', '8', '2', '9', '1', '7'].map((d, i) => (
          <div key={i} className="w-10 h-12 md:w-11 md:h-12 rounded-md border border-[#1e2a24] bg-[#0b1310] flex items-center justify-center text-lg font-bold text-white">
            {d}
          </div>
        ))}
      </div>
      <div className="w-full bg-[#059669] text-white font-semibold text-sm py-2.5 rounded-lg text-center">
        Verify &amp; Access Document
      </div>
    </div>
  </div>
);

// ─── Mockup 3: Document review (Step 03 / 04) ───────────────────────────────
const DocumentReviewMockup = () => {
  const approvals = [
    { name: 'Jamie Park',  role: 'Voice Talent',    status: 'pending',  date: null },
    { name: 'Sarah Kim',   role: 'Founder',         status: 'approved', date: 'Approved — Jun 20, 2026' },
    { name: 'Raj Patel',   role: 'Legal Counsel',   status: 'pending',  date: null },
  ];
  return (
    <div className="bg-[#111c17] border border-[#1e2a24] rounded-xl shadow-2xl overflow-hidden" data-testid="ai-mockup-document-review">
      <div className="px-5 py-4 border-b border-[#1e2a24]">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-base font-semibold text-white truncate">Voice Cloning Consent — Production</p>
            <p className="text-xs text-slate-500 mt-1 leading-relaxed">
              Voice-actor consent + likeness license for production deployment of v3 model. Governed by California law.
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-[10px] font-mono text-slate-400">v2</span>
            <StatusBadge tone="draft">draft</StatusBadge>
          </div>
        </div>
        <div className="mt-4 bg-amber-500/10 border-l-4 border-amber-400 rounded p-3">
          <p className="text-[10px] font-semibold text-amber-300 uppercase tracking-wide mb-1.5">
            Reviewer Instructions
          </p>
          <p className="text-xs text-amber-100 leading-relaxed">
            Please confirm the scope of voice use in Clause 3.2 and the compensation
            and opt-out terms in Clause 5. Talent signs as the Contributor.
          </p>
        </div>
      </div>
      <div className="px-5 py-4 border-b border-[#1e2a24]">
        <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-3">Approval Status</p>
        <div className="space-y-2">
          {approvals.map((a, i) => (
            <div key={i} className="flex items-center justify-between text-xs">
              <div className="min-w-0">
                <p className="text-white font-medium truncate">{a.name}</p>
                <p className="text-slate-500 text-[11px]">{a.role}</p>
              </div>
              <div className="text-right shrink-0">
                {a.status === 'approved' ? (
                  <span className="inline-flex items-center gap-1 text-[#059669] font-medium">
                    <CheckCircle2 className="w-3.5 h-3.5" /> {a.date}
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-slate-400">
                    <Clock className="w-3.5 h-3.5" /> Pending
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="px-5 py-4 flex flex-col sm:flex-row gap-2.5">
        <div className="flex-1 bg-[#059669] text-white font-semibold text-sm py-2.5 rounded-lg inline-flex items-center justify-center gap-1.5">
          <CheckCircle2 className="w-4 h-4" /> Approve Document
        </div>
        <div className="flex-1 border border-[#1e2a24] text-slate-300 font-semibold text-sm py-2.5 rounded-lg text-center">
          Request Changes
        </div>
      </div>
    </div>
  );
};

// ─── Mockup 4: Sign Digitally (Step 05) ─────────────────────────────────────
const SignDigitallyMockup = () => (
  <div
    className="bg-[#111c17] border border-[#1e2a24] rounded-xl shadow-2xl p-5 md:p-6"
    data-testid="ai-mockup-sign-digitally"
  >
    <div className="flex items-center gap-2.5 mb-1">
      <div className="w-8 h-8 rounded-lg bg-[#059669]/15 border border-[#059669]/40 flex items-center justify-center">
        <PenLine className="w-4 h-4 text-[#059669]" />
      </div>
      <div>
        <p className="text-sm font-semibold text-white">Sign digitally</p>
        <p className="text-[11px] text-slate-500">Typed name rendered as your signature.</p>
      </div>
    </div>
    <div className="my-4 border-t border-[#1e2a24]" />
    <div className="mb-3">
      <p className="text-[11px] text-slate-400 mb-1">Full name (editable)</p>
      <div className="bg-[#0b1310] border border-[#1e2a24] rounded px-3 py-2 text-sm text-white">
        Jamie Park
      </div>
    </div>
    <div className="mb-3">
      <p className="text-[11px] text-slate-400 mb-1">Title (optional)</p>
      <div className="bg-[#0b1310] border border-[#1e2a24] rounded px-3 py-2 text-sm text-white">
        Voice Talent
      </div>
    </div>
    <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-1.5">
      Live preview · this is how your signature will appear
    </p>
    <div className="bg-[#0b1310] border border-[#1e2a24] rounded p-4 mb-4 flex items-center">
      <span
        style={{
          fontFamily: '"Great Vibes", serif',
          fontSize: '36px',
          color: '#e2e8f0',
          lineHeight: 1,
        }}
      >
        Jamie Park
      </span>
    </div>
    <div className="flex items-start gap-2 mb-4">
      <div className="w-4 h-4 rounded border border-[#059669] bg-[#059669]/20 flex items-center justify-center mt-0.5 shrink-0">
        <CheckCircle2 className="w-3 h-3 text-[#059669]" />
      </div>
      <p className="text-[11px] text-slate-400 leading-relaxed">
        I confirm that the typed name above will serve as my electronic signature
        and has the same legal effect as a handwritten signature.
      </p>
    </div>
    <div className="w-full inline-flex items-center justify-center gap-2 bg-[#059669] text-white font-semibold text-sm py-2.5 rounded-lg">
      <ShieldCheck className="w-4 h-4" /> Adopt &amp; Sign
    </div>
  </div>
);

// ─── Mockup 5: Execute Agreement (Step 06) ──────────────────────────────────
const ExecuteAgreementMockup = () => (
  <div
    className="bg-[#111c17] border border-[#1e2a24] rounded-xl shadow-2xl p-5 md:p-6 space-y-4"
    data-testid="ai-mockup-execute-agreement"
  >
    <div className="bg-blue-500/5 border border-blue-400/30 rounded-lg p-3">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold text-blue-100 inline-flex items-center gap-2">
          <PenLine className="w-3.5 h-3.5 text-blue-300" /> Sign digitally
        </p>
        <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-500/15 text-blue-200 border border-blue-400/30">
          Signed: 3 of 3
        </span>
      </div>
      <div className="mt-2 flex items-start gap-1.5 text-[11px] text-[#059669]">
        <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 shrink-0" />
        <span>All parties have signed — anyone can now execute.</span>
      </div>
    </div>
    <div className="bg-[#059669]/10 border border-[#059669]/40 rounded-lg p-4">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-full bg-[#059669]/25 border border-[#059669] flex items-center justify-center shrink-0">
          <Stamp className="w-4 h-4 text-[#059669]" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-[#059669]">
            All parties have signed · ready to execute
          </p>
          <p className="text-[11px] text-slate-300 mt-1 leading-relaxed">
            Stamps the document with the StartEase Agent execution seal,
            appends the signed signature page, and generates the completion
            certificate. Irreversible.
          </p>
          <div className="mt-3 inline-flex items-center gap-1.5 bg-[#059669] text-white font-semibold text-xs py-2 px-3 rounded-md">
            <ShieldCheck className="w-3.5 h-3.5" /> Execute Agreement
          </div>
        </div>
      </div>
    </div>
    <div className="bg-[#0b1310] border border-[#1e2a24] rounded-lg p-3 flex items-center gap-3">
      <div className="w-8 h-8 rounded bg-[#059669]/15 border border-[#059669]/40 flex items-center justify-center shrink-0">
        <Download className="w-4 h-4 text-[#059669]" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-xs font-medium text-white">Voice Cloning Consent — Production (executed)</p>
        <p className="text-[10px] text-slate-500">
          Stamped PDF + signature page + completion certificate
        </p>
      </div>
      <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#059669]/15 text-[#059669] border border-[#059669]/30 shrink-0">
        Executed
      </span>
    </div>
  </div>
);

// ── Content data (per locked CONTENT BLOCK) ──────────────────────────────────
// AI_CATEGORIES ids match PM-provided deep-link targets (#voice, #video-face, ...)
// Amber trigger lines match the spec verbatim, with em dashes replaced by periods
// per the PM's "no em dashes in marketing copy" standing style rule.
const AI_CATEGORIES = [
  {
    key: 'voice',
    id: 'voice',
    icon: Mic,
    title: 'Voice / audio AI',
    trigger: 'ELVIS Act + FCC AI-voice rules. Consent before any cloned voice ships or calls.',
    documents: [
      'Voice cloning consent',
      'Voice-likeness license',
      'Voice-actor / contributor agreement (compensation + opt-out)',
      'Written biometric consent + destruction schedule',
      'Outbound-calling consent + disclosure terms where the AI places calls',
    ],
  },
  {
    key: 'video',
    id: 'video-face',
    icon: Video,
    title: 'Video / avatar / face AI',
    trigger: 'Right-of-publicity + biometric laws. A face is protected data in several states.',
    documents: [
      'Digital-replica / likeness license',
      'Talent and face release',
      'Output-ownership terms',
      'Biometric consent + destruction schedule',
    ],
  },
  {
    key: 'image',
    id: 'image-art',
    icon: ImageIcon,
    title: 'Image / art generation',
    trigger: 'TAKE IT DOWN Act + copyright exposure on training data and outputs.',
    documents: [
      'Training-data license',
      'Output-infringement indemnity terms',
      'End-user content license',
      'Sharing-and-publication policy',
      'DMCA / copyright-takedown policy',
    ],
  },
  {
    key: 'search',
    id: 'search-engines',
    icon: Search,
    title: 'AI search / answer engines',
    trigger: 'Publisher content rights. Licensing before your engine quotes and cites.',
    documents: [
      'Content-licensing agreements with the publishers it cites and uses',
    ],
  },
  {
    key: 'coding',
    id: 'coding',
    icon: Code2,
    title: 'AI coding assistants',
    trigger: 'Open-source license obligations flow into your customers\u2019 codebases.',
    documents: [
      'Code-output license with open-source attribution and indemnity',
      '"Your code is never used to train" warranty',
    ],
  },
  {
    key: 'finetune',
    id: 'fine-tuning',
    icon: SlidersHorizontal,
    title: 'AI that fine-tunes on customer data',
    trigger: 'Data-use limits. Who owns the tuned model decides who owns the business.',
    documents: [
      'Fine-tuning agreement (who owns the tuned model)',
      'Model-escrow agreement',
    ],
  },
  {
    key: 'reseller',
    id: 'api-resellers',
    icon: Repeat,
    title: 'AI built on a foundation-model API, resold',
    trigger: 'Upstream terms flow down. Your customer contract must match your vendor\u2019s.',
    documents: [
      'Flow-down / reseller terms',
      'End User Agreement the customer puts in place with its own users',
      'Customer agreement aligned to the upstream vendor',
    ],
  },
  {
    key: 'data',
    id: 'data-labeling',
    icon: Database,
    title: 'Data / labeling / RLHF supply',
    trigger: 'IP assignment gaps here surface in diligence, not before.',
    documents: [
      'Data annotation services agreement',
      'Data licensing agreement',
      'IP assignment',
      'Contractor / labeler agreements',
    ],
  },
  {
    key: 'model-provider',
    id: 'model-api',
    icon: Cpu,
    title: 'Model / API provider',
    trigger: 'Your AUP and developer terms are your first line when a customer misuses the model.',
    documents: [
      'Model license for the weights',
      'API / developer terms with rate limits',
      'Acceptable Use Policy',
      'EULA',
      'Plugin / connector / actions terms',
      'Sharing-and-publication policy',
    ],
  },
  {
    key: 'robotics',
    id: 'robotics',
    icon: Bot,
    title: 'Robotics / hardware AI',
    trigger: 'Product liability. Warnings and service terms carry the risk.',
    documents: [
      'Hardware-supplier agreement',
      'Firmware IP assignment',
      'Product warning and disclaimer terms',
      'Service and maintenance agreement',
    ],
  },
  {
    key: 'cybersecurity',
    id: 'cybersecurity',
    icon: ShieldAlert,
    title: 'Cybersecurity AI',
    trigger: 'Liability splitting. Who answers when the AI misses the breach.',
    documents: [
      'Service agreement splitting client vs. company responsibility',
      'Limitation-of-liability terms',
      'Sensitive-log handling terms',
    ],
  },
  {
    key: 'governance',
    id: 'governance',
    icon: Scale,
    title: 'AI governance / safety tools',
    trigger: 'Accuracy claims are enforceable claims. Service levels must match reality.',
    documents: [
      'Service agreement with detection-accuracy service levels',
      'Clause placing final responsibility on the customer',
    ],
  },
];

const REGULATED_VERTICALS = [
  {
    key: 'legal',
    title: 'Legal AI',
    extra: 'Unauthorized-practice-of-law disclaimer + output-reliance terms.',
  },
  {
    key: 'health',
    title: 'Health AI',
    extra: 'HIPAA Business Associate Agreement; patient data-use terms.',
  },
  {
    key: 'education',
    title: 'Education AI',
    extra: 'Student Data Privacy Agreement (FERPA); parental-consent terms where minors use it.',
  },
  {
    key: 'hiring',
    title: 'Hiring AI',
    extra: 'Candidate notice and consent; FCRA disclosure and authorization forms.',
  },
  {
    key: 'financial',
    title: 'Financial AI',
    extra: 'Investment-advice disclaimer; financial data-use terms.',
  },
];

const BASE_LAYERS = [
  {
    key: 'terms',
    id: 'layer-terms',
    title: 'The terms layer',
    trigger: 'The contracts between you and everyone who uses or buys the product.',
    documents: [
      'Terms of Use (consumer)',
      'Business Terms / Services Agreement (B2B)',
      'Master Service Agreement + Order Form',
      'Service-specific supplemental terms',
      'End User Agreement',
    ],
  },
  {
    key: 'policy',
    id: 'layer-policy',
    title: 'The policy layer',
    trigger: 'What regulators and platforms check first.',
    documents: [
      'Acceptable Use Policy',
      'Privacy Policy',
      'Cookie Policy',
      'Data Processing Agreement with a no-training warranty',
      'Sharing-and-publication policy',
      'DMCA / copyright-takedown policy',
    ],
  },
  {
    key: 'ownership',
    id: 'layer-ownership',
    title: 'The ownership and commercial layer',
    trigger: 'Who owns the model, the outputs, and the revenue.',
    documents: [
      'EULA',
      'Output-ownership terms',
      'IP assignment from everyone who touched the code or model',
      'Brand / trademark usage guidelines',
      'Billing / refund terms',
      'Beta / early-access terms',
      'NDA',
    ],
  },
];

const VAULT_STEPS = [
  {
    icon: FileText,
    title: 'Form',
    body: 'Incorporation, IP assignment, NDAs.',
  },
  {
    icon: Layers,
    title: 'Operate',
    body: 'The terms, policies, licenses and consents above.',
  },
  {
    icon: Lock,
    title: 'Lock and store',
    body: 'Every executed document in one room — reviewed, approved, signed, sealed, ready for diligence.',
  },
  {
    icon: RefreshCcw,
    title: 'Refresh',
    body: 'Documents updated as your product and the law change, with full version history.',
  },
];

// ── Page ─────────────────────────────────────────────────────────────────────
const AiDocumentServicesPage = () => {
  const { open: openGuide } = useGuideRequest();
  const [enquiryOpen, setEnquiryOpen] = useState(false);
  return (
  <div className={`${BG} min-h-screen text-white`} data-testid="ai-document-services-page">
    <SEOHead
      title="AI Legal Compliance & Document Drafting for AI Companies | StartEase Agent"
      description="74 US laws already apply to AI products — voice, chatbots, hiring, health, minors, lending. Free Domain Risk Check names the laws your product triggers and the documents they demand."
      canonical="https://starteaseai.com/ai-document-services"
    />

    <Header />

    {/* ─── HERO ────────────────────────────────────────────────────────── */}
    <section className="relative overflow-hidden pt-20 pb-20 md:pt-24 md:pb-24" data-testid="ai-hero-section">
      {/* Subtle grid background */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage:
            'linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)',
          backgroundSize: '56px 56px',
          WebkitMaskImage: 'radial-gradient(ellipse 75% 65% at 50% 30%, #000 30%, transparent 78%)',
          maskImage: 'radial-gradient(ellipse 75% 65% at 50% 30%, #000 30%, transparent 78%)',
        }}
        aria-hidden="true"
      />
      <div className="container-custom max-w-5xl text-center relative">
        <div
          className="inline-flex items-center gap-2 border border-white/10 rounded-full px-4 py-2 mb-8"
          data-testid="ai-hero-badge"
        >
          <span className="w-1.5 h-1.5 rounded-full bg-[#34d399]" aria-hidden="true" />
          <span className="font-mono text-[11px] tracking-[0.2em] text-slate-400">
            AI LEGAL COMPLIANCE
          </span>
        </div>

        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.07] mb-6" data-testid="ai-hero-h1">
          <span className="block text-white">Your AI product triggers US laws.</span>
          <span className="block text-[#059669] mt-1.5">We track them. We draft what they demand.</span>
        </h1>

        <p className="text-[17px] leading-[1.65] text-slate-400 max-w-2xl mx-auto mt-7">
          74 US laws are already live for AI products — voice consents, chatbot disclosures,
          bias audits, minors&apos; rules, health and lending regulations. Most founders find
          out from a regulator or an investor&apos;s counsel. Here&apos;s how to find out first.
        </p>

        {/* Stats strip */}
        <div className="grid grid-cols-1 md:grid-cols-3 border border-white/10 rounded-xl overflow-hidden max-w-[640px] mx-auto mt-9 bg-[#111c17]" data-testid="ai-hero-stats">
          {[
            { num: '74', cap: 'Laws on watch' },
            { num: '84', cap: 'AI domains mapped' },
            { num: '9',  cap: 'Drafting packs' },
          ].map((s, i, arr) => (
            <div key={s.cap} className={`p-6 ${i < arr.length - 1 ? 'md:border-r border-white/10 border-b md:border-b-0' : ''}`}>
              <div className="font-serif text-[38px] leading-none text-[#059669]">{s.num}</div>
              <div className="font-mono text-[10.5px] tracking-[0.18em] text-slate-400 mt-2.5 uppercase">{s.cap}</div>
            </div>
          ))}
        </div>

        {/* CTAs — amber Risk Check + ghost book-a-call */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3.5 mt-9" data-testid="ai-hero-ctas">
          <button
            type="button"
            onClick={() => openGuide('ai-document-services')}
            className="inline-flex items-center gap-2 font-semibold text-sm md:text-[15px] px-6 py-3.5 rounded-lg transition-colors bg-[#059669] text-white hover:bg-[#047857]"
            data-testid="ai-hero-cta-risk"
          >
            Run My Free Risk Check <ArrowRight className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => setEnquiryOpen(true)}
            className="inline-flex items-center gap-2 border border-white/10 hover:border-[#059669]/60 text-white font-semibold text-sm md:text-[15px] px-6 py-3.5 rounded-lg transition-colors"
            data-testid="ai-hero-cta-call"
          >
            Map your documents — let’s talk
          </button>
        </div>

        {/* Law chips */}
        <div className="flex flex-wrap gap-2.5 justify-center mt-10" data-testid="ai-hero-law-chips">
          {[
            { label: 'ELVIS ACT',        hot: true },
            { label: 'SB 243',           hot: false },
            { label: 'COPPA 2025',       hot: false },
            { label: 'LOCAL LAW 144',    hot: true },
            { label: 'TRAIGA',           hot: false },
            { label: 'TAKE IT DOWN ACT', hot: false },
          ].map((c) => (
            <span
              key={c.label}
              className="font-mono text-[10.5px] tracking-[0.16em] border rounded px-3 py-1.5"
              style={{
                borderColor: c.hot ? 'rgba(5,150,105,0.5)' : 'rgba(255,255,255,0.10)',
                color: c.hot ? '#34d399' : '#94a3b8',
              }}
            >
              {c.label}
            </span>
          ))}
        </div>
      </div>
    </section>

    {/* ─── THE REGISTER — laws by domain (new) ─────────────────────────── */}
    <section className="bg-[#070d0b] border-y border-white/5 py-20 md:py-24" data-testid="ai-register-section">
      <div className="container-custom max-w-6xl">
        <div className="max-w-2xl mb-12">
          <span className="font-sans text-[11px] font-semibold uppercase tracking-[0.22em]" style={{ color: '#34d399' }}>The Register</span>
          <h2 className="font-serif text-3xl md:text-[42px] font-semibold text-white tracking-tight leading-[1.12] mt-3 mb-4">
            The laws, domain by domain.
          </h2>
          <p className="text-slate-400 text-base leading-[1.65]">
            These are the six domains where US law reaches AI products first — and the specific
            laws doing the reaching.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-12 items-start">
          <ul className="list-none" data-testid="ai-register-domains">
            {[
              { term: 'Voice & likeness',      body: 'cloning consents, ELVIS Act, FCC AI-voice rules' },
              { term: 'Chatbots & companions', body: 'SB 243 disclosures, crisis protocols' },
              { term: 'Hiring & HR AI',        body: 'NYC Local Law 144 bias audits, Colorado AI Act' },
              { term: 'Kids & minors',         body: 'COPPA 2025 amendments, age-gating terms' },
              { term: 'Health & wellness',     body: 'FDA guidance, HIPAA add-ons, HHS scenarios' },
              { term: 'Fintech & lending',     body: 'CFPB adverse-action rules, NAIC AI bulletin' },
            ].map((d) => (
              <li key={d.term} className="flex items-start gap-3 py-3.5 border-b border-white/5 text-[14.5px] text-slate-400">
                <span className="font-mono text-[#059669] shrink-0 mt-0.5">▸</span>
                <span>
                  <span className="text-white font-semibold">{d.term}</span>
                  <span className="text-slate-400"> — {d.body}</span>
                </span>
              </li>
            ))}
          </ul>

          <div
            className="rounded-xl p-7"
            style={{
              border: '1px solid rgba(5,150,105,0.45)',
              background: 'linear-gradient(180deg, rgba(5,150,105,0.06), transparent)',
            }}
            data-testid="ai-register-risk-box"
          >
            <span className="font-sans text-[11px] font-semibold uppercase tracking-[0.22em]" style={{ color: '#34d399' }}>Free guide · 2 minutes</span>
            <h3 className="font-serif text-[22px] text-white mt-3 mb-2.5">Free Domain Risk Check</h3>
            <p className="text-[14.5px] text-slate-400 leading-[1.65] mb-6">
              Enter your email and get our free guide: the 8 AI product domains, the US laws on
              each one, and the documents they demand. Then reply to that email with what you&apos;re
              building, and we name your exact laws. Free, no call, no obligation.
            </p>
            {/* Rewired to GuideRequest modal (Free Guide Email Capture) */}
            <button
              type="button"
              onClick={() => openGuide('ai-document-services')}
              className="inline-flex items-center gap-2 border font-semibold text-sm md:text-[15px] px-6 py-3.5 rounded-lg transition-colors hover:bg-[#34d399]/10"
              style={{ borderColor: '#34d399', color: '#34d399' }}
              data-testid="ai-register-risk-cta"
            >
              Run My Free Risk Check <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </section>

    {/* ─── FIND YOUR AI ─────────────────────────────────────────────────── */}
    <section className={`${BG_PANEL} border-y ${BORDER} py-20 md:py-28`} data-testid="ai-find-your-ai-section">
      <div className="container-custom max-w-6xl">
        <div className="mb-12 md:mb-16">
          <SectionLabel>Find your AI</SectionLabel>
          <h2 className="font-serif text-3xl md:text-4xl font-semibold text-white tracking-tight leading-tight mt-4 mb-3">
            Find your AI, find your laws, find your documents.
          </h2>
          <p className="text-base text-slate-400 leading-relaxed max-w-2xl">
            Pick what your company does with AI. Each category shows the laws pulling on it
            and the documents they demand.
          </p>
        </div>

        <AiAccordion items={AI_CATEGORIES} testIdPrefix="ai-cat" />
      </div>
    </section>

    {/* ─── REGULATED VERTICALS (lighter pill/list) ───────────────────────── */}
    <section className={`${BG} py-20 md:py-28`} data-testid="ai-regulated-section">
      <div className="container-custom max-w-4xl">
        <div className="mb-10">
          <SectionLabel>Regulated verticals</SectionLabel>
          <h2 className="font-serif text-3xl md:text-4xl font-semibold text-white tracking-tight leading-tight mt-4 mb-3">
            The document the sector forces on top.
          </h2>
          <p className="text-base text-slate-400 leading-relaxed max-w-2xl">
            If your AI ships into one of these verticals, regulators add a
            specific document on top of the base stack — non-negotiable, sector
            by sector.
          </p>
        </div>

        <ul className="space-y-3">
          {REGULATED_VERTICALS.map((v) => (
            <li
              key={v.key}
              className={`${BG_PANEL} border ${BORDER} rounded-lg px-5 py-4 flex items-start gap-4 hover:border-slate-600 transition-colors`}
              data-testid={`ai-regulated-${v.key}`}
            >
              <span
                className="text-[10px] font-semibold uppercase tracking-wider text-slate-300 bg-slate-800/80 border border-slate-700 rounded-full px-2.5 py-1 shrink-0 mt-0.5 whitespace-nowrap"
              >
                {v.title}
              </span>
              <span className="text-sm text-slate-300 leading-relaxed">
                {v.extra}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </section>

    {/* ─── BASE LAYERS — every AI company ────────────────────────────────── */}
    <section className={`${BG_PANEL} border-y ${BORDER} py-20 md:py-28`} data-testid="ai-base-layers-section">
      <div className="container-custom max-w-6xl">
        <div className="mb-12 md:mb-16">
          <SectionLabel>Every AI company</SectionLabel>
          <h2 className="font-serif text-3xl md:text-4xl font-semibold text-white tracking-tight leading-tight mt-4 mb-3">
            The documents every AI company needs drafted.
          </h2>
          <p className="text-base text-slate-400 leading-relaxed max-w-2xl">
            Three layers. Each one has to cross-reference the others. Get any
            of them wrong and the rest fall apart.
          </p>
        </div>

        <AiAccordion items={BASE_LAYERS} testIdPrefix="ai-layer" />
      </div>
    </section>

    {/* ─── LEGAL VAULT — sequencing ─────────────────────────────────────── */}
    <section className={`${BG} py-20 md:py-28`} data-testid="ai-vault-section">
      <div className="container-custom max-w-5xl">
        <div className="mb-12">
          <SectionLabel>The legal vault</SectionLabel>
          <h2 className="font-serif text-3xl md:text-4xl font-semibold text-white tracking-tight leading-tight mt-4 mb-3">
            The order you build it in.
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10">
          {VAULT_STEPS.map((s, i) => (
            <div
              key={s.title}
              className="flex gap-5"
              data-testid={`ai-vault-step-${i + 1}`}
            >
              <div
                className="w-11 h-11 rounded-lg bg-slate-800/80 border border-slate-700 flex items-center justify-center shrink-0"
                aria-hidden="true"
              >
                <s.icon className="w-5 h-5 text-slate-300" strokeWidth={1.5} />
              </div>
              <div>
                <h4 className="text-lg font-semibold text-white mb-1.5">
                  {s.title}
                </h4>
                <p className="text-sm text-slate-400 leading-relaxed">
                  {s.body}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ─── WHY YOU CAN'T SELF-SERVE — callout ───────────────────────────── */}
    <section className={`${BG_PANEL} border-y ${BORDER} py-20 md:py-24`} data-testid="ai-self-serve-section">
      <div className="container-custom max-w-3xl">
        <SectionLabel>Why a template site can't do this</SectionLabel>
        <h2 className="font-serif text-3xl md:text-4xl font-semibold text-white tracking-tight leading-tight mt-4 mb-6">
          Why you can't self-serve this.
        </h2>
        <p className="text-base md:text-lg text-slate-300 leading-relaxed">
          A US AI company needs dozens of custom legal documents that cross-reference each other
          and track laws that changed within the last eighteen months. A template can&apos;t know
          which laws reach your product, can&apos;t write for your specific business, and can&apos;t
          lock an approval trail.{' '}
          <span className="text-white font-semibold">Fixed-fee packs, scoped on one call. No hourly billing.</span>
        </p>
      </div>
    </section>

    {/* ─── HOW IT WORKS (4 steps, drafting flow) ────────────────────────── */}
    <section className={`${BG} py-20 md:py-28`} data-testid="ai-how-it-works-section">
      <div className="container-custom max-w-5xl">
        <div className="mb-12 md:mb-16 max-w-3xl">
          <SectionLabel>How it works</SectionLabel>
          <h2 className="font-serif text-3xl md:text-4xl font-semibold text-white tracking-tight leading-tight mt-4">
            Four steps. One clean document. Nothing on WhatsApp.
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-10 mb-10">
          <Step
            testId="ai-how-step-1"
            n="1"
            title="Book a call"
            body="30 minutes. We walk through what your AI company actually does — voice, video, model, data — and map the laws and documents your business needs."
          />
          <Step
            testId="ai-how-step-2"
            n="2"
            title="We draft"
            body="Every document written for your specific AI business — your model, your data, your customer contract, your state's governing law. Not a template, not AI output."
          />
          <Step
            testId="ai-how-step-3"
            n="3"
            title="Review in your Data Room"
            body="Drafts delivered into a private, secure Data Room. Founders, counsel, talent and customers log in with OTP. No account needed. Review, comment, request changes. Everything tracked."
          />
          <Step
            testId="ai-how-step-4"
            n="4"
            title="Approve, sign and lock"
            body="All parties approve. Each party signs digitally. The document is executed and stamped. Completion certificate generated. Full audit trail locked permanently."
          />
        </div>

        {/* TODO(attorney): when the attorney-of-record arrangement is active, change to:
            "Every document is prepared by our drafting team and reviewed and finalized by
            a licensed US attorney before delivery." */}
        <p
          className="text-xs text-slate-500 leading-relaxed italic max-w-2xl pt-6 border-t border-[#1e2a24]"
          data-testid="ai-attorney-footnote"
        >
          StartEase Agent is not a law firm and does not provide legal advice.
          Documents are prepared by our drafting team.
        </p>
      </div>
    </section>

    {/* ─── DATA ROOM FEATURE ─────────────────────────────────────────────── */}
    <section className={`${BG_PANEL} border-y ${BORDER} py-20 md:py-24`} data-testid="ai-data-room-feature-section">
      <div className="container-custom max-w-6xl">
        <div className="mb-12 max-w-3xl">
          <SectionLabel>Data Room — included with every drafting order.</SectionLabel>
          <h2 className="font-serif text-3xl md:text-4xl font-semibold text-white tracking-tight leading-tight mt-4 mb-5">
            Every approval locked. Permanently.
          </h2>
          <p className="text-base md:text-lg text-slate-300 leading-relaxed">
            When your document is ready, it goes into a private, secure Data
            Room. Your talent, customers, co-founders and counsel log in with a
            6-digit OTP — no account needed. They review, comment, and approve.
            Every response is locked with a timestamp the moment it&apos;s
            submitted.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <ReasonCard
            testId="ai-data-room-feature-card-1"
            title="OTP Login — No Passwords"
            body="Reviewers receive one email. They enter their OTP and they're in. No account creation, no password to remember."
          />
          <ReasonCard
            testId="ai-data-room-feature-card-2"
            title="Version Control"
            body="Every draft preserved. When you upload a revised version, all parties are notified and re-approval begins. Old versions never deleted."
          />
          <ReasonCard
            testId="ai-data-room-feature-card-3"
            title="Completion Certificate"
            body="When all parties approve and sign, a branded certificate is generated automatically and sent to everyone. Legally defensible record."
          />
          <ReasonCard
            testId="ai-data-room-feature-card-4"
            title="Full Audit Trail"
            body="Every action logged permanently — who viewed, who commented, who approved, who signed, and when. Export as branded PDF at any time."
          />
        </div>

        <p
          className="mt-12 text-lg md:text-xl font-semibold text-white text-center"
          data-testid="ai-data-room-feature-end-line"
        >
          No WhatsApp. No email chains. No &lsquo;I never saw that version.&rsquo;
        </p>
        <p className="mt-4 text-center">
          <a
            href="/login"
            className="text-sm text-slate-500 hover:text-[#059669] transition-colors inline-flex items-center gap-1"
            data-testid="ai-data-room-feature-cta-link"
          >
            Already a StartEase client? Access your Data Room <ArrowRight className="w-3.5 h-3.5" />
          </a>
        </p>
      </div>
    </section>

    {/* ─── SEE IT IN ACTION (condensed to 3 steps per PM spec) ───────────── */}
    <section
      className={`${BG} py-20 md:py-28`}
      data-testid="ai-see-it-in-action-section"
    >
      <div className="container-custom max-w-6xl">
        <div className="mb-16 max-w-3xl">
          <SectionLabel>See it in action</SectionLabel>
          <h2 className="font-serif text-3xl md:text-4xl font-semibold text-white tracking-tight leading-tight mt-4 mb-3">
            From document to signed approval. In three steps.
          </h2>
          <p className="text-base md:text-lg text-slate-400 leading-relaxed">
            Here&apos;s exactly what happens after your document is drafted.
          </p>
        </div>

        <div className="space-y-20 md:space-y-28">
          {/* NEW STEP 01 — merges old 01 (Data Room list) + 02 (email + OTP). */}
          <StepBlock
            testId="ai-step-01-row"
            number="01"
            title="Your document lands in a secure Data Room. Reviewers get one email."
            body="Once drafted, the document is uploaded into a private Data Room for your company with version tracking on. Your talent, customer, or counsel receives a single email with a secure link. They enter a 6-digit OTP and they&rsquo;re inside the document. No account, no password."
            mockup={
              <div className="space-y-5">
                <DocumentListMockup />
                <EmailInviteAndOtpMockup />
              </div>
            }
            mockupOnRightDesktop
          />

          {/* NEW STEP 02 — kept from old 03 (review + approve). Old step 04 deleted. */}
          <StepBlock
            testId="ai-step-02-row"
            number="02"
            title="Your reviewer sees the document. Every action timestamped."
            body="Inside the Data Room, your reviewer sees the document, your reviewer instructions, and one decision. Approve or Request Changes. Every action is logged with a timestamp the moment it happens. Nothing can be backdated."
            mockup={<DocumentReviewMockup />}
            mockupOnRightDesktop={false}
          />

          {/* NEW STEP 03 — merges old 05 (sign digitally) + 06 (execute agreement). */}
          <StepBlock
            testId="ai-step-03-row"
            number="03"
            title="Each party signs digitally. One click executes and locks the certificate forever."
            body="Every signer pre-fills their name, sees their signature rendered live, and clicks Adopt &amp; Sign. Legal e-signature record with timestamp and IP captured server-side. Once everyone has signed, anyone can execute. One click stamps the document with the StartEase execution seal, appends the signed signature page, generates a tamper-proof completion certificate, and locks the audit trail permanently."
            mockup={
              <div className="space-y-5">
                <SignDigitallyMockup />
                <ExecuteAgreementMockup />
              </div>
            }
            mockupOnRightDesktop
          />
        </div>

        <div className="mt-20 md:mt-24 text-center">
          <p
            className="font-serif text-2xl md:text-3xl font-semibold text-white tracking-tight mb-7"
            data-testid="ai-see-it-end-line"
          >
            Your AI company. Documented. Approved. Locked.
          </p>
          <div className="flex justify-center">
            <PrimaryCTA testId="ai-see-it-cta" onClick={() => setEnquiryOpen(true)}>Book a consultation</PrimaryCTA>
          </div>
        </div>
      </div>
    </section>

    {/* ─── FINAL CTA ────────────────────────────────────────────────────── */}
    <section className="bg-[#070d0b] border-t border-white/5 py-20 md:py-24" data-testid="ai-final-cta-section">
      <div className="container-custom max-w-3xl text-center">
        <span className="font-sans text-[11px] font-semibold uppercase tracking-[0.22em]" style={{ color: '#34d399' }}>Find your laws</span>
        <h2 className="font-serif text-3xl md:text-[42px] font-semibold text-white tracking-tight leading-[1.12] mt-3 mb-5">
          Find out which US laws your AI product is inside.
        </h2>
        <p className="text-slate-400 text-base leading-[1.65] mb-9 max-w-[620px] mx-auto">
          Free guide first, then your personal Risk Check: reply to the guide email with what
          you&apos;re building and we name the laws, the documents they demand, and the pack
          that covers you. Or book a call and map it live.
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3.5">
          {/* Rewired to GuideRequest modal (Free Guide Email Capture) */}
          <button
            type="button"
            onClick={() => openGuide('ai-document-services')}
            className="inline-flex items-center gap-2 font-semibold text-sm md:text-[15px] px-6 py-3.5 rounded-lg transition-colors bg-[#059669] text-white hover:bg-[#047857]"
            data-testid="ai-final-cta-risk"
          >
            Run My Free Risk Check <ArrowRight className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => setEnquiryOpen(true)}
            className="inline-flex items-center gap-2 border border-white/10 hover:border-[#059669]/60 text-white font-semibold text-sm md:text-[15px] px-6 py-3.5 rounded-lg transition-colors"
            data-testid="ai-final-cta-call"
          >
            Let’s discuss
          </button>
        </div>
      </div>
    </section>

    <Footer />
    <AiEnquiryModal open={enquiryOpen} onOpenChange={setEnquiryOpen} />
  </div>
  );
};

export default AiDocumentServicesPage;
